import React, {useState, useEffect} from 'react';
import {StyleSheet, ScrollView, RefreshControl} from 'react-native';
import {Box, HStack, Heading, Pressable, VStack} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {
  BalanceSummaryIcon,
  FilterCircleFilledIcon,
  FilterCircleIcon,
  RemoveFilterCircleIcon,
} from './../../../../../components/icons';
import SemaphoreItem from './SemaphoreItem';
import {useSalesmanContext} from '../../../../../context/salesman';
import useToast from '../../../../../hooks';
import {ISemaphoreValue} from '../../../../../interfaces/dtos';
import {SemaphoreKeys} from '../../../../../constants/enumerations';
import {getSemaphoreColor} from '../../../../../constants/semaphore';
import * as Keychain from '../../../../../utils/keychain';
import DashboardTemplate from '../../../../../components/templates/dashboard';
import {
  DashboardItem,
  SummaryAccountItem,
} from '../../../../../components/dashboard';
import {StackParamList} from '../../../../../navigators';
import {useContextState as useUserContext} from '../../../../../context/user';
import {SpinnerModal} from '../../../../../components/modals';
import {message} from '../../../../../utils/messages';

type ScreenProps = NativeStackScreenProps<
  StackParamList,
  'SalesmanPortfolioDashboard'
>;

export const DashboardScreen = ({navigation}: ScreenProps) => {
  const {signOff, notifications, fetchNotificationList, profile} =
    useUserContext();

  const {
    fetchBalanceSheet,
    balanceSheet,
    fetchSemaphoreItems,
    semaphoreItems,
    salesmanPortfolioFilter,
    setPortfolioFilter,
  } = useSalesmanContext();

  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const {showServerError} = useToast();

  const getNotifications = async () => {
    await fetchNotificationList();
  };

  const getIndicators = async () => {
    if (await fetchBalanceSheet(salesmanPortfolioFilter)) {
      if (await fetchSemaphoreItems(salesmanPortfolioFilter)) {
        return;
      }
    }
    throw new Error('Internal error');
  };

  const getData = async () => {
    try {
      setIsLoading(true);
      await getNotifications();
      await getIndicators();
      setIsLoading(false);
    } catch (ex) {
      setIsLoading(false);
      showServerError();
    }
  };

  const refresInfoData = async () => {
    try {
      setIsLoading(true);
      await getIndicators();
      setIsLoading(false);
    } catch (ex) {
      setIsLoading(false);
      showServerError();
    }
  };

  const refreshData = async () => {
    await getData();
    setRefreshing(false);
  };

  useEffect(() => {
    if (semaphoreItems == null) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semaphoreItems]);

  useEffect(() => {
    if (refreshing) {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshing]);

  useEffect(() => {
    refresInfoData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesmanPortfolioFilter]);

  const handleTapNotifications = () => {
    navigation.navigate('Notifications');
  };

  const handleShowProfile = () => {
    navigation.navigate('Profile');
  };

  const handleLogOut = () => {
    signOff();
    Keychain.resetGenericPassword();
  };

  const getCompanyName = () => {
    if (profile && profile.company) {
      return profile.company;
    }
    return '';
  };

  const getUserName = () => {
    if (profile && profile.firstName) {
      return `${profile?.firstName}`;
    }
    return '';
  };

  const handleTapProdile = () => {
    handleShowProfile();
  };

  const handleOnPress = (position: number) => {
    if (semaphoreItems) {
      const item = semaphoreItems[position];
      if (item) {
        navigation.navigate('SalesmanDebtors', {position});
      }
    }
  };

  const handleOnRefresh = async () => {
    setRefreshing(true);
  };

  const getSemaphoreItems = (): ISemaphoreValue[] => {
    if (semaphoreItems && !isLoading) {
      return semaphoreItems;
    }

    return [
      {
        order: 0,
        key: SemaphoreKeys.Red,
        value: 0,
        amount: '---',
      },
      {
        order: 0,
        key: SemaphoreKeys.Orange,
        value: 0,
        amount: '---',
      },
      {
        order: 0,
        key: SemaphoreKeys.Yellow,
        value: 0,
        amount: '---',
      },
      {
        order: 0,
        key: SemaphoreKeys.Green,
        value: 0,
        amount: '---',
      },
    ];
  };

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleOnFilterPress = () => {
    navigation.navigate('PortfolioFilter');
  };

  const FilterIcon = () => {
    if (salesmanPortfolioFilter) {
      return <FilterCircleFilledIcon width={30} style={styles.balanceIcon} />;
    }
    return <FilterCircleIcon width={30} style={styles.balanceIcon} />;
  };

  const handleOnClearFilterPress = () => {
    setPortfolioFilter(null);
  };

  return (
    <DashboardTemplate
      title={getUserName()}
      summary={getCompanyName()}
      notifications={notifications ? notifications : []}
      onTapNotifications={() => handleTapNotifications()}
      onTapLogout={() => handleLogOut()}
      onTapProfile={() => handleTapProdile()}
      backButton={{
        onPress: () => handleGoBack(),
        label: 'back-dashboard',
      }}>
      <ScrollView
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleOnRefresh}
            tintColor="transparent"
          />
        }>
        <SpinnerModal
          show={isLoading}
          title={
            balanceSheet && semaphoreItems ? message('updating') : undefined
          }
        />
        {
          <Box borderRadius="md" marginTop={22}>
            <VStack space={3} justifyContent="center" marginX={4}>
              <DashboardItem
                label="dashboard-summary-account-title"
                icon={
                  <BalanceSummaryIcon width={40} style={styles.balanceIcon} />
                }
                shadow={true}>
                <SummaryAccountItem
                  localBalance={!isLoading ? balanceSheet?.local : '---'}
                  foreignBalance={!isLoading ? balanceSheet?.foreign : '---'}
                />
              </DashboardItem>
              <HStack justifyContent={'space-between'} alignItems={'center'}>
                <Heading fontSize={16} marginTop={0} color="#06355E">
                  {message('Accounts receivable semaphore title')}
                </Heading>
                <HStack paddingBottom={2} paddingRight={3} space={2}>
                  {salesmanPortfolioFilter && (
                    <Pressable onPress={() => handleOnClearFilterPress()}>
                      <RemoveFilterCircleIcon
                        width={30}
                        style={styles.balanceIcon}
                      />
                    </Pressable>
                  )}
                  <Pressable onPress={() => handleOnFilterPress()}>
                    <FilterIcon />
                  </Pressable>
                </HStack>
              </HStack>
              {getSemaphoreItems()?.map(
                (item: ISemaphoreValue, position: number) => {
                  return (
                    <DashboardItem key={`dashboard-${item.key}`}>
                      <SemaphoreItem
                        {...{
                          ...item,
                          label: `label-${item.key}`,
                          color: getSemaphoreColor(item.key),
                          position,
                        }}
                        onPress={handleOnPress}
                      />
                    </DashboardItem>
                  );
                },
              )}
            </VStack>
          </Box>
        }
      </ScrollView>
    </DashboardTemplate>
  );
};

const styles = StyleSheet.create({
  balanceIcon: {
    marginTop: 10,
  },
});
